import { useEffect, useState, type FormEvent } from 'react';
import { DISPOSITION_VALUES } from '@tips-crm/shared';

export function DispositionForm({
  current,
  suggested,
  onSubmit,
  isSubmitting,
  dispositions = [...DISPOSITION_VALUES],
}: {
  current?: string | null;
  /** AI-suggested disposition — preselected while the rep hasn't chosen one. */
  suggested?: string | null;
  onSubmit: (disposition: string, note?: string) => void | Promise<void>;
  isSubmitting?: boolean;
  dispositions?: string[];
}) {
  const [disposition, setDisposition] = useState(current ?? '');
  const [note, setNote] = useState('');

  // The AI suggestion loads async; preselect it only while nothing is chosen yet.
  useEffect(() => {
    if (!current && suggested && dispositions.includes(suggested)) {
      setDisposition((prev) => prev || suggested);
    }
  }, [current, suggested, dispositions]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!disposition) return;
    await onSubmit(disposition, note.trim() || undefined);
    setNote('');
  }

  return (
    <form className="disposition-form" onSubmit={handleSubmit}>
      <label className="field">
        <span className="field__label">Disposition</span>
        <select
          className="field__input"
          value={disposition}
          onChange={(e) => setDisposition(e.target.value)}
          disabled={isSubmitting}
        >
          <option value="">Select a disposition…</option>
          {dispositions.map((d) => (
            <option key={d} value={d}>
              {d.replace(/_/g, ' ')}
              {suggested === d && !current ? ' (AI suggested)' : ''}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span className="field__label">Outcome note (optional)</span>
        <textarea
          className="field__input"
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={isSubmitting}
        />
      </label>
      <button type="submit" className="btn btn--primary" disabled={isSubmitting || !disposition}>
        {isSubmitting ? 'Saving…' : 'Save disposition'}
      </button>
    </form>
  );
}
